import { Box, Stack, useMediaQuery, type Theme } from '@mui/material';
import { RefreshCw } from 'lucide-react';
import { useCallback, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router';

import type { AdminApi, MetricsPeriodKey } from '@/admin/adminApi';
import { useHttpClient } from '@/app/providers';
import { Button, PageHeader } from '@/ui/kit';

import { ActivityCard, KindsCard } from './ActivityCards';
import { AttentionCard } from './AttentionCard';
import { EXCLUDED_AUDIT_ACTION, PERIODS, parsePeriod } from './dashboardModel';
import { HealthBand } from './HealthBand';
import { MetricTiles } from './MetricTiles';
import { PeriodSwitch } from './PeriodSwitch';
import { RecentActionsCard } from './RecentActionsCard';
import { useAdminRead } from './useAdminRead';

/**
 * Tableau de bord (P02 ; P19 en mode sombre ; P20 sous 600 px).
 *
 * Il repond a trois questions — le service va-t-il bien, l'usage progresse-t-il,
 * qu'est-ce qui m'attend — et **ne previent personne** : il se consulte, il
 * n'alerte pas.
 *
 * - **Quatre requetes, pas une de plus** : sante, agregats, « a traiter »,
 *   journal. Les agregats alimentent a eux seuls le bandeau (API), les tuiles,
 *   l'activite et la repartition. Chaque lecture a son etat et son « Réessayer » :
 *   un bloc lent ou en panne n'efface pas les autres.
 * - **Aucune relecture automatique** (ADR 0035 §4) : chaque lecture d'une route
 *   `/admin/*` ecrit une ligne d'audit. « Actualiser » relit tout ; la periode
 *   (`?period=`) ne relit que les agregats. L'heure de verification, ecrite dans le
 *   bandeau, dit si la page est fraiche.
 * - **P19 n'est pas une variante** : le mode sombre est le meme ecran lu avec les
 *   jetons du theme sombre. Aucune couleur n'est ecrite ici ni dans les
 *   graphiques (`noHexLiteral.test.ts`).
 * - **P20 n'est pas une route ni un second composant** : sous 600 px, les memes
 *   blocs se composent autrement (sante en une ligne, deux tuiles, « À traiter »,
 *   deux actions) et les blocs d'activite **ne sont pas montes**.
 */

const AUDIT_ROWS = 5;

const longDate = (date: Date): string => {
  const text = date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return text.charAt(0).toLocaleUpperCase('fr-FR') + text.slice(1);
};

export const DashboardScreen = (): ReactElement => {
  const http = useHttpClient();
  const narrow = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();
  const period = parsePeriod(searchParams.get('period'));
  const [refreshToken, setRefreshToken] = useState(0);
  // Posee une fois : un titre qui change de jour sous les yeux n'apporte rien.
  const [today] = useState(() => new Date());

  const readHealth = useCallback((api: AdminApi) => api.health(), []);
  const readMetrics = useCallback((api: AdminApi) => api.metrics(period), [period]);
  const readAttention = useCallback((api: AdminApi) => api.attention(), []);
  const readEvents = useCallback(
    (api: AdminApi) => api.listAuditEvents({ excludeAction: EXCLUDED_AUDIT_ACTION }, 1, AUDIT_ROWS),
    [],
  );

  const health = useAdminRead(readHealth, refreshToken);
  const metrics = useAdminRead(readMetrics, refreshToken);
  const attention = useAdminRead(readAttention, refreshToken);
  const events = useAdminRead(readEvents, refreshToken);

  const busy = [health, metrics, attention, events].some((read) => read.state.kind === 'loading');
  const email = http.session()?.user.email ?? null;

  const changePeriod = (next: MetricsPeriodKey): void => {
    setSearchParams({ period: next }, { replace: true });
  };

  return (
    <Stack spacing={3} sx={{ mt: 1, mb: 4 }}>
      <PageHeader
        eyebrow="Pilotage"
        title="Bonjour"
        subtitle={email === null ? longDate(today) : `${email} · ${longDate(today)}`}
        actions={
          <>
            <PeriodSwitch value={period} onChange={changePeriod} options={PERIODS} />
            <Button
              icon={RefreshCw}
              disabled={busy}
              onClick={() => {
                setRefreshToken((previous) => previous + 1);
              }}
            >
              Actualiser
            </Button>
          </>
        }
      />

      <HealthBand health={health.state} metrics={metrics.state} narrow={narrow} onRetry={health.reload} />
      <MetricTiles metrics={metrics.state} period={period} narrow={narrow} onRetry={metrics.reload} />

      {narrow ? (
        <>
          <AttentionCard attention={attention.state} narrow onRetry={attention.reload} />
          <RecentActionsCard events={events.state} narrow onRetry={events.reload} />
        </>
      ) : (
        <>
          <Box sx={{ display: 'grid', gap: 2, alignItems: 'start', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 3fr) minmax(0, 2fr)' } }}>
            <ActivityCard metrics={metrics.state} onRetry={metrics.reload} />
            <AttentionCard attention={attention.state} narrow={false} onRetry={attention.reload} />
          </Box>
          <Box sx={{ display: 'grid', gap: 2, alignItems: 'start', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' } }}>
            <KindsCard metrics={metrics.state} period={period} onRetry={metrics.reload} />
            <RecentActionsCard events={events.state} narrow={false} onRetry={events.reload} />
          </Box>
        </>
      )}
    </Stack>
  );
};
